import { useState } from "react";
import { motion } from "framer-motion";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useAccount } from "wagmi";
import { formatEther } from "viem";
import {
    Users, Link as LinkIcon, Copy, Check, Loader2, ArrowUpRight,
    AlertTriangle, TrendingUp,
} from "lucide-react";
import {
    useReferralData,
    useRegisterCode,
    useClaimReferralRewards,
} from "@/hooks/useReferrals";
import { ADDRESSES, CHAIN_ID, EXPLORER_TX } from "@/lib/config";
import { useFireOnConfirm } from "@/hooks/useFireOnConfirm";
import { fmtCompact } from "@/lib/format";
import { CountUp } from "@/components/CountUp";
import { EthMark } from "@/components/Logo";

export default function Referrals() {
    const { address, isConnected, chainId } = useAccount();
    const wrongChain = isConnected && chainId !== CHAIN_ID;
    const noContract = !ADDRESSES.REFERRAL_REGISTRY;
    const { data, refetch } = useReferralData();
    const hasCode = data.myCodeString.length > 0;

    return (
        <section className="max-w-5xl mx-auto px-6 py-16">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-12"
            >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-pink mb-4 text-xs font-semibold text-piggy-pink-200">
                    <Users className="w-3 h-3" />
                    15% of every taxed swap → referrers
                </div>
                <h1 className="display text-5xl md:text-6xl font-black tracking-tighter mb-4">
                    Rally your merry band. <span className="gradient-text-shimmer">Earn ETH.</span>
                </h1>
                <p className="text-piggy-cream/60 text-lg max-w-2xl mx-auto">
                    Register a short code. Share it. Every buy attributed to you
                    pays you ETH pro-rata, automatically. Achievement badges
                    boost your share up to 2× — Cosmic Piggy referrers earn the
                    most per swap.
                </p>
            </motion.div>

            {noContract ? (
                <NoticeBox>
                    Referral contract not yet deployed. Add{" "}
                    <code className="text-piggy-pink-300">VITE_REFERRAL_REGISTRY_ADDRESS</code>{" "}
                    to your <code className="text-piggy-pink-300">.env</code> once it's live.
                </NoticeBox>
            ) : (
                <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6">
                    <StatsCard data={data} address={address} />
                    <div className="space-y-6">
                        {!hasCode ? (
                            <RegisterCard
                                connected={isConnected}
                                wrongChain={wrongChain}
                                onSuccess={refetch}
                            />
                        ) : (
                            <ShareCard codeString={data.myCodeString} />
                        )}
                        <ClaimCard
                            earned={data.earned}
                            multiplierBps={data.multiplierBps}
                            connected={isConnected}
                            wrongChain={wrongChain}
                            onSuccess={refetch}
                        />
                    </div>
                </div>
            )}

            <HowItWorks />
        </section>
    );
}

// Shared action-button gate: disconnected → connect; wrong chain → switch;
// otherwise the real action passed as children.
function GateButton({
    connected, wrongChain, action, children,
}: {
    connected: boolean; wrongChain: boolean; action: string; children: React.ReactNode;
}) {
    if (!connected) {
        return (
            <ConnectButton.Custom>
                {({ openConnectModal }) => (
                    <button onClick={openConnectModal} className="w-full btn-primary justify-center">
                        Connect wallet to {action}
                    </button>
                )}
            </ConnectButton.Custom>
        );
    }
    if (wrongChain) {
        return (
            <ConnectButton.Custom>
                {({ openChainModal }) => (
                    <button onClick={openChainModal} className="w-full btn-primary justify-center">
                        Switch to Robinhood Chain
                    </button>
                )}
            </ConnectButton.Custom>
        );
    }
    return <>{children}</>;
}

function NoticeBox({ children }: { children: React.ReactNode }) {
    return (
        <div className="glass-pink rounded-3xl p-6 flex gap-3 items-start max-w-2xl mx-auto">
            <AlertTriangle className="w-5 h-5 text-piggy-pink-300 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-piggy-cream/80">{children}</div>
        </div>
    );
}

function StatsCard({
    data, address,
}: {
    data: ReturnType<typeof useReferralData>["data"];
    address?: string;
}) {
    return (
        <div className="glass rounded-3xl p-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-4">
                Your performance
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
                <Stat label="Referred buys"
                      value={<CountUp value={Number(data.buyCount)} format={(n) => n.toFixed(0)} />} />
                <Stat label="Volume (PIGGY)"
                      value={<CountUp value={Number(data.volume) / 1e18} format={(n) => fmtCompact(n)} />} />
                <Stat label="Unclaimed (ETH)"
                      value={<CountUp value={Number(data.earned) / 1e18} format={(n) => n.toFixed(4)} />}
                      accent />
                <Stat label="Boost multiplier"
                      value={`${(data.multiplierBps / 10000).toFixed(2)}×`} />
            </div>

            <div className="text-xs text-piggy-cream/40 mb-1 uppercase tracking-wider font-semibold">
                Your wallet
            </div>
            <div className="font-mono text-sm text-piggy-cream/80 break-all">
                {address ?? "Connect your wallet to see your position."}
            </div>
        </div>
    );
}

function Stat({
    label, value, accent,
}: {
    label: string; value: React.ReactNode; accent?: boolean;
}) {
    return (
        <div className={`rounded-2xl p-4 ${accent ? "bg-piggy-pink-500/[0.08] ring-1 ring-piggy-pink-500/30" : "bg-white/[0.03]"}`}>
            <div className="text-xs text-piggy-cream/40 uppercase tracking-wider mb-1">{label}</div>
            <div className={`display text-xl font-bold ${accent ? "gradient-text" : "text-piggy-cream"}`}>
                {value}
            </div>
        </div>
    );
}

function RegisterCard({
    connected, wrongChain, onSuccess,
}: { connected: boolean; wrongChain: boolean; onSuccess: () => void }) {
    const [code, setCode] = useState("");
    const { register, status, hash } = useRegisterCode();
    const busy = status === "pending" || status === "confirming";
    const done = status === "confirmed";

    useFireOnConfirm(done, onSuccess);

    const normalized = code.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 12);
    const valid = normalized.length >= 3;

    return (
        <div className="glass rounded-3xl p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-3">
                Register your code
            </div>
            <p className="text-sm text-piggy-cream/60 mb-4">
                3–12 characters. Letters, digits, dash, underscore. One per
                wallet, on-chain, forever — pick something memorable.
            </p>
            <div className="flex gap-2 mb-4">
                <span className="px-3 py-3 rounded-2xl bg-white/[0.04] text-piggy-cream/40 font-mono text-sm grid place-items-center">
                    PIG-
                </span>
                <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="ALICE"
                    maxLength={8}
                    className="flex-1 px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/10 focus:border-piggy-pink-500/40 outline-none font-mono uppercase tracking-wider text-piggy-cream"
                />
            </div>
            <GateButton connected={connected} wrongChain={wrongChain} action="register a code">
                <button
                    onClick={() => register(`PIG-${normalized.toUpperCase()}`)}
                    disabled={!valid || busy}
                    className="w-full btn-primary justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {done ? <Check className="w-4 h-4" /> : null}
                    {busy ? "Confirming..." : done ? "Code registered!" : `Register PIG-${normalized.toUpperCase() || "____"}`}
                </button>
            </GateButton>
            {hash && (
                <a
                    href={`${EXPLORER_TX}/${hash}`}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-3 flex items-center justify-center gap-1 text-xs text-piggy-cream/50 hover:text-piggy-cream"
                >
                    View on Explorer <ArrowUpRight className="w-3 h-3" />
                </a>
            )}
        </div>
    );
}

function ShareCard({ codeString }: { codeString: string }) {
    const [copied, setCopied] = useState(false);
    const url = `https://safepiggy.xyz/?ref=${codeString}`;

    const copy = () => {
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2_000);
    };

    return (
        <div className="glass rounded-3xl p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-3">
                Your referral link
            </div>
            <div className="flex items-center gap-2 mb-4 p-3 rounded-2xl bg-white/[0.04] border border-white/10">
                <LinkIcon className="w-4 h-4 text-piggy-cream/40" />
                <input
                    type="text" readOnly value={url}
                    className="flex-1 bg-transparent outline-none text-sm text-piggy-cream font-mono"
                />
                <button
                    onClick={copy}
                    className="px-3 py-1.5 rounded-xl bg-piggy-pink-500/20 hover:bg-piggy-pink-500/30 text-piggy-pink-200 text-xs font-semibold flex items-center gap-1"
                >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? "Copied" : "Copy"}
                </button>
            </div>
            <div className="text-xs text-piggy-cream/50">
                Share on{" "}
                <a className="text-piggy-pink-300 hover:text-piggy-pink-200"
                   href={`https://x.com/intent/tweet?text=Six%20earning%20paths%2C%20one%20piggy.%20Mint%20a%20Piggy%20and%20earn%20ETH%20on%20Robinhood%20Chain.&url=${encodeURIComponent(url)}`}
                   target="_blank" rel="noopener noreferrer">
                    X
                </a>{" "}
                or{" "}
                <a className="text-piggy-pink-300 hover:text-piggy-pink-200"
                   href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=Six%20earning%20paths%2C%20one%20piggy`}
                   target="_blank" rel="noopener noreferrer">
                    Telegram
                </a>
                {" "}— each buy your referees make earns you ETH for 30 days.
            </div>
        </div>
    );
}

function ClaimCard({
    earned, multiplierBps, connected, wrongChain, onSuccess,
}: {
    earned: bigint; multiplierBps: number; connected: boolean; wrongChain: boolean; onSuccess: () => void;
}) {
    const { claim, status, hash } = useClaimReferralRewards();
    const busy = status === "pending" || status === "confirming";
    const done = status === "confirmed";
    const hasEarned = earned > 0n;

    useFireOnConfirm(done, onSuccess);

    return (
        <div className="glass rounded-3xl p-6">
            <div className="flex items-baseline justify-between mb-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50">
                    Unclaimed rewards
                </div>
                <div className="text-xs text-piggy-cream/40 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    {(multiplierBps / 10000).toFixed(2)}× boost
                </div>
            </div>
            <div className="display text-4xl font-black gradient-text mb-1 inline-flex items-center gap-2">
                {formatEther(earned).slice(0, 8)} <EthMark size={28} />
                <span>ETH</span>
            </div>
            <div className="text-xs text-piggy-cream/40 mb-5">
                Subject to treasury throttle at claim time
            </div>
            <GateButton connected={connected} wrongChain={wrongChain} action="claim">
                <button
                    onClick={claim}
                    disabled={!hasEarned || busy}
                    className="w-full btn-gold justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {done && <Check className="w-4 h-4" />}
                    {busy ? "Confirming..." : done ? "Claimed!" : hasEarned ? "Claim ETH" : "Nothing to claim"}
                </button>
            </GateButton>
            {hash && (
                <a
                    href={`${EXPLORER_TX}/${hash}`}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-3 flex items-center justify-center gap-1 text-xs text-piggy-cream/50 hover:text-piggy-cream"
                >
                    View on Explorer <ArrowUpRight className="w-3 h-3" />
                </a>
            )}
        </div>
    );
}

function HowItWorks() {
    const steps = [
        { n: 1, t: "Register your code", b: "Pick a memorable handle. On-chain forever. One per wallet." },
        { n: 2, t: "Share your link",    b: "Post it in X, Telegram, every corner of the shire. Each click attributes when the user makes their first buy." },
        { n: 3, t: "Earn ETH pro-rata",  b: "15% of all taxed swaps is split among referrers, proportional to attributed volume." },
        { n: 4, t: "Unlock badge boosts", b: "Hit 10/50/200/1000/5000/25000 referred buys to mint achievement NFTs that multiply your share." },
    ];
    return (
        <div className="mt-20">
            <h2 className="display text-3xl font-black mb-8 text-center">How it works</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {steps.map((s) => (
                    <div key={s.n} className="glass rounded-2xl p-5">
                        <div className="display text-3xl font-black gradient-text mb-2">{s.n}</div>
                        <div className="display font-bold mb-1 text-piggy-cream">{s.t}</div>
                        <div className="text-xs text-piggy-cream/60 leading-relaxed">{s.b}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}
